import { Permissions, useAuthWrapper } from '@weareopenr/authkit';

// hasPermission only accepts the Permissions enum, which does not yet expose
// roles:create or roles:update. Until @weareopenr/authkit ships those members this
// is the single place we assert a raw slug, keeping the cast isolated and typed.
const toPermission = (slug: string): Permissions => slug as Permissions;

const ROLES_CREATE = toPermission('roles:create');
const ROLES_UPDATE = toPermission('roles:update');

/**
 * Custom hook that provides permission checks for various application features
 * @returns Object containing boolean permission flags
 */
export const usePermissions = () => {
  const { hasPermission, hasAllPermissions } = useAuthWrapper();

  const canViewUsersAndRoles = hasAllPermissions([Permissions.USERS_READ, Permissions.ROLES_READ]);
  const canCreateUsers = hasPermission(Permissions.USERS_CREATE);
  const canEditUsers = hasPermission(Permissions.USERS_UPDATE);
  const canDeactivateUsers = hasPermission(Permissions.USERS_UPDATE);
  const canViewRoles = hasPermission(Permissions.ROLES_READ);
  const canCreateRoles = hasPermission(ROLES_CREATE);
  const canEditRoles = hasPermission(ROLES_UPDATE);
  const canCreateBrands = hasPermission(Permissions.BRANDS_CREATE);
  const canEditBrands = hasPermission(Permissions.BRANDS_UPDATE);
  const canDeleteBrands = hasPermission(Permissions.BRANDS_DELETE);
  const canInstallExtensions = hasPermission(Permissions.EXTENSIONS_INSTALL);
  const canEditExtensions = hasPermission(Permissions.EXTENSIONS_UPDATE);
  const canUninstallExtensions = hasPermission(Permissions.EXTENSIONS_UNINSTALL);
  const canViewUsers = hasPermission(Permissions.USERS_READ);

  return {
    canCreateBrands,
    canEditBrands,
    canDeleteBrands,
    canViewUsersAndRoles,
    canCreateUsers,
    canEditUsers,
    canDeactivateUsers,
    canViewRoles,
    canCreateRoles,
    canEditRoles,
    canInstallExtensions,
    canEditExtensions,
    canUninstallExtensions,
    canViewUsers,
  };
};
